import { isNil, some } from 'lodash-es';

export default () => {
  /**
   * Checks if any of the specified fields in the given object are `null` or `undefined`.
   *
   * @template T - The type of the object being checked.
   * @param {T} obj - The object to check.
   * @param {(keyof T)[]} fieldsToCheck - An array of keys representing the fields to check.
   * @returns {boolean} - Returns `true` if at least one field is `null` or `undefined`, otherwise `false`.
   */
  const hasNullOrUndefinedFields = <T extends Record<string, any>>(
    obj: T,
    fieldsToCheck: (keyof T)[],
  ): boolean => {
    if (Object.keys(obj).length === 0) {
      return false; // Handle empty objects
    }
    return some(fieldsToCheck, (field) => obj[field] == null);
  };

  /**
   * Checks if an object is incomplete (invalid).
   *
   * - An object is considered incomplete if:
   *   1. It is empty (has no keys).
   *   2. It contains null or undefined values for any of the specified fields.
   *
   * @template T - The type of the object to check.
   * @param {T} obj - The object to validate.
   * @param {(keyof T)[]} fieldsToCheck - The list of fields to validate for null or undefined values.
   * @returns {boolean} - `true` if the object is incomplete, otherwise `false`.
   */
  const isObjectIncomplete = <T extends Record<string, any>>(
    obj: T,
    fieldsToCheck: (keyof T)[],
  ): boolean => {
    return (
      isNil(obj) || Object.keys(obj).length === 0 || hasNullOrUndefinedFields(obj, fieldsToCheck)
    );
  };

  return { hasNullOrUndefinedFields, isObjectIncomplete };
};
